import React, { useMemo } from 'react';
import {
  ChevronRightIcon,
  Collapse,
  HomeWorkIcon,
  Input,
  LedgerHeader,
  LockIcon,
  SearchIcon,
  Switch,
  Text,
  ledgerContainerClass,
} from '@billda/design-system';
import { useSiteReimportStore } from '../../../stores/siteReimportStore';
import type { PlotDiff } from '../../../types/reimportTypes';
import ReimportLabourItemRow, {
  REIMPORT_COLLAPSE,
  REIMPORT_COLUMNS,
} from './ReimportLabourItemRow';
import ReimportPlotRow from './ReimportPlotRow';

interface ReimportDiffTableProps {
  plots: PlotDiff[];
}

export const getEffectivePlots = (plots: PlotDiff[]): PlotDiff[] =>
  plots
    .map(plot => {
      const labourItems = plot.labourItems.filter(item => !item.isProtected);
      const hasChangedItems = labourItems.some(
        item => item.changeType !== 'Unchanged'
      );
      const isWholePlotChange =
        (plot.changeType === 'Added' || plot.changeType === 'Removed') &&
        labourItems.length > 0;

      return {
        ...plot,
        labourItems,
        hasProtectedItems: false,
        changeType: isWholePlotChange
          ? plot.changeType
          : hasChangedItems
            ? 'Modified'
            : 'Unchanged',
      };
    })
    .filter(plot => plot.labourItems.length > 0);

export const countEffectiveChanges = (plots: PlotDiff[]): number =>
  getEffectivePlots(plots).reduce(
    (count, plot) =>
      count +
      plot.labourItems.filter(item => item.changeType !== 'Unchanged').length,
    0
  );

const ReimportDiffTable: React.FC<ReimportDiffTableProps> = ({ plots }) => {
  const { filterText, setFilterText } = useSiteReimportStore();
  const [showUnchanged, setShowUnchanged] = React.useState(false);
  const [showSkipped, setShowSkipped] = React.useState(false);

  const effectivePlots = useMemo(() => getEffectivePlots(plots), [plots]);
  const skippedPlots = useMemo(
    () =>
      plots
        .map(plot => ({
          ...plot,
          labourItems: plot.labourItems.filter(
            item => item.isProtected && item.changeType !== 'Unchanged'
          ),
        }))
        .filter(plot => plot.labourItems.length > 0),
    [plots]
  );
  const skippedItemCount = skippedPlots.reduce(
    (count, plot) => count + plot.labourItems.length,
    0
  );

  const filteredPlots = useMemo(() => {
    const search = filterText.trim().toLowerCase();

    return effectivePlots.flatMap(plot => {
      const plotMatches =
        !search ||
        plot.plotNumber.toLowerCase().includes(search) ||
        plot.plotName.toLowerCase().includes(search);
      const labourItems = plot.labourItems.filter(
        item =>
          (showUnchanged || item.changeType !== 'Unchanged') &&
          (plotMatches || item.definitionName.toLowerCase().includes(search))
      );

      return labourItems.length > 0 ? [{ ...plot, labourItems }] : [];
    });
  }, [effectivePlots, showUnchanged, filterText]);

  const unchangedItemCount = effectivePlots.reduce(
    (count, plot) =>
      count +
      plot.labourItems.filter(item => item.changeType === 'Unchanged').length,
    0
  );
  const effectiveChangeCount = countEffectiveChanges(plots);

  return (
    <div data-testid="reimport-diff-table">
      <div className="mb-3">
        <Text variant="body1" className="font-semibold">
          Changes by plot
        </Text>
        <Text variant="body2" color="secondary">
          Open a plot to compare its current and imported pricing.
        </Text>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="w-full sm:w-75">
          <Input
            inputSize="sm"
            placeholder="Search plots and labour items"
            value={filterText}
            onChange={e => setFilterText(e.target.value)}
            aria-label="Search plots and labour items"
            startAdornment={
              <SearchIcon
                className="size-4 text-content-secondary"
                aria-hidden="true"
              />
            }
            data-testid="reimport-diff-filter"
          />
        </div>

        {unchangedItemCount > 0 && (
          <label className="flex cursor-pointer items-center gap-2">
            <Switch
              checked={showUnchanged}
              onCheckedChange={checked => setShowUnchanged(checked)}
              data-testid="reimport-show-unchanged-toggle"
            />
            <Text variant="body2" color="secondary">
              Show unchanged ({unchangedItemCount})
            </Text>
          </label>
        )}

        <Text variant="body2" color="secondary" className="ml-auto">
          {filteredPlots.length} of {effectivePlots.length} plots shown
        </Text>
      </div>

      <div
        className={`overflow-hidden rounded-2xl border border-border-default ${ledgerContainerClass(REIMPORT_COLLAPSE.container)}`}
      >
        {filteredPlots.length > 0 && (
          <LedgerHeader
            columns={REIMPORT_COLUMNS}
            collapse={REIMPORT_COLLAPSE}
            titleLabel="Labour item"
            valueLabel="Change"
          />
        )}
        {filteredPlots.map(plot => (
          <ReimportPlotRow
            key={plot.plotNumber}
            plot={plot}
            showUnchangedItems={showUnchanged}
          />
        ))}
        {filteredPlots.length === 0 && (
          <div className="px-4 py-8 text-center">
            <Text variant="body2" color="secondary">
              {filterText.trim()
                ? 'No plots or labour items match this search'
                : effectiveChangeCount === 0
                  ? 'No changes to apply'
                  : 'No changes to show'}
            </Text>
            {!filterText.trim() && effectiveChangeCount === 0 && (
              <Text variant="caption" color="secondary">
                The CSV matches the current site pricing.
              </Text>
            )}
          </div>
        )}
      </div>

      {skippedItemCount > 0 && (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowSkipped(current => !current)}
            aria-expanded={showSkipped}
            className={`flex w-full items-center gap-3 border border-border-warning-strong bg-surface-warning-subtle p-3 text-left ${
              showSkipped ? 'rounded-t-lg' : 'rounded-2xl'
            }`}
          >
            <LockIcon
              className="size-4 text-content-warning"
              aria-hidden="true"
            />
            <div className="flex-1">
              <Text variant="body2" className="font-semibold">
                {skippedItemCount} requested change
                {skippedItemCount === 1 ? '' : 's'} will be skipped
              </Text>
              <Text variant="caption" color="secondary">
                Active allocations protect the current pricing.
              </Text>
            </div>
            <ChevronRightIcon
              className={`size-4 text-content-secondary transition-transform duration-150 ${
                showSkipped ? 'rotate-90' : ''
              }`}
              aria-hidden="true"
            />
          </button>
          <Collapse in={showSkipped} unmountOnExit>
            <div
              className={`overflow-hidden rounded-b-lg border border-t-0 border-border-default ${ledgerContainerClass(REIMPORT_COLLAPSE.container)}`}
            >
              <LedgerHeader
                columns={REIMPORT_COLUMNS}
                collapse={REIMPORT_COLLAPSE}
                titleLabel="Skipped labour item"
                valueLabel="Result"
              />
              {skippedPlots.map(plot => (
                <div key={plot.plotNumber}>
                  <div className="flex min-h-9.5 items-center gap-2 border-b border-border-default bg-surface-raised px-4 py-1.5">
                    <HomeWorkIcon
                      className="size-4 text-content-secondary"
                      aria-hidden="true"
                    />
                    <Text variant="body2" className="font-semibold">
                      {plot.plotNumber} — {plot.plotName}
                    </Text>
                  </div>
                  {plot.labourItems.map(item => (
                    <ReimportLabourItemRow
                      key={item.definitionName}
                      item={item}
                    />
                  ))}
                </div>
              ))}
            </div>
          </Collapse>
        </div>
      )}
    </div>
  );
};

export default ReimportDiffTable;
